import { Stack } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import { Controller, FieldValues, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ClockAddIcon } from '@/shared/assets/icons/clock-add-icon';
import { Alert } from '@/shared/components/ui/alert/alert';
import { IconButton } from '@/shared/components/ui/button/icon-button/icon-button';
import { FormDateTimePickerActionBar } from '@/shared/components/ui/form/components/form-date-time-picker/components/form-date-time-picker-action-bar/form-date-time-picker-action-bar';
import { FormDateTimePickerTabs } from '@/shared/components/ui/form/components/form-date-time-picker/components/form-date-time-picker-tabs/form-date-time-picker-tabs';
import {
  FormDateTimePickerContainer,
  StyledStaticDateTimePicker,
} from '@/shared/components/ui/form/components/form-date-time-picker/form-date-time-picker.styled';
import { TFormDateTimePickerProps } from '@/shared/components/ui/form/components/form-date-time-picker/form-date-time-picker.types';
import { FormLabel } from '@/shared/components/ui/form/components/form-label/form-label';
import { PopoverController } from '@/shared/components/ui/popover/components/popover-controller/popover-controller';
import { useDateUtils } from '@/shared/hooks/use-date-utils/use-date-utils';
import { is12HourFormat } from '@/shared/hooks/use-date-utils/use-date-utils.utils';
import { convertDateFormat } from '@/shared/utils/format';

export const FormDateTimePicker = <TFieldValues extends FieldValues>({
  name,
  label,
  defaultValue,
  rules,
  shouldUnregister,
  hint,
  triggerIcon,
  triggerLabel,
  dateTimePickerProps,
  futureOnly = false,
  maxDays,
  triggerElement,
  required,
  ...rest
}: TFormDateTimePickerProps<TFieldValues>) => {
  const { t, i18n } = useTranslation();
  const { control, trigger } = useFormContext<TFieldValues>();
  const { dateTimeFormat } = useDateUtils();
  const locale = i18n.language.split('-')[0];

  const handleDateChange = (
    value: dayjs.Dayjs | null,
    onChange: (value: string | null) => void,
  ) => {
    if (value) {
      // The picker has no seconds view, so drop the seconds it inherits from now.
      const pickedDate = dayjs(value).startOf('minute');
      let convertedDate = convertDateFormat(pickedDate.toDate().toString());

      const isInPast = pickedDate.isBefore(dayjs(), 'minute');

      if (isInPast && futureOnly) {
        const nextValidDate = dayjs()
          .add(5 - (dayjs().minute() % 5), 'minute')
          .startOf('minute');
        convertedDate = convertDateFormat(nextValidDate.toDate().toString());
      }

      onChange(convertedDate);
    } else {
      onChange(null);
    }
    void trigger(name);
  };

  const getDatePickerProps = () => {
    const props = { ...dateTimePickerProps };

    if (futureOnly) {
      props.minDateTime = dayjs();
    }

    if (maxDays && maxDays > 0) {
      props.maxDateTime = dayjs().add(maxDays, 'day').endOf('day');
    }

    return props;
  };

  const renderTrigger = (value: string | null) => {
    if (triggerElement) {
      return triggerElement(value);
    }

    const className = value ? 'active-icon-button' : undefined;

    if (triggerLabel) {
      return (
        <IconButton className={className}>
          {triggerIcon || <ClockAddIcon />}
          {triggerLabel(value)}
        </IconButton>
      );
    }

    return (
      <IconButton
        aria-label={label || t('ui:select_date')}
        className={className}
        tooltip={label}
      >
        {triggerIcon || <ClockAddIcon />}
      </IconButton>
    );
  };

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      rules={rules}
      shouldUnregister={shouldUnregister}
      render={({ field: { onChange, value }, fieldState: { error } }) => (
        <FormDateTimePickerContainer gap="0.75rem" width="100%">
          <Stack
            direction={triggerElement ? 'column' : 'row'}
            gap="0.5rem"
            alignItems="center"
          >
            <LocalizationProvider
              dateAdapter={AdapterDayjs}
              adapterLocale={locale}
            >
              <Stack width={triggerElement ? '100%' : 'auto'}>
                {triggerElement && label && (
                  <FormLabel label={label} required={required} />
                )}
                <PopoverController trigger={renderTrigger(value)} {...rest}>
                  {(handleClose) => (
                    <StyledStaticDateTimePicker
                      ampm={is12HourFormat(dateTimeFormat)}
                      value={value ? dayjs(value) : null}
                      onChange={(newValue) =>
                        handleDateChange(dayjs(newValue), onChange)
                      }
                      {...getDatePickerProps()}
                      localeText={{
                        toolbarTitle: t('ui:form.select_date_time'),
                      }}
                      slots={{
                        actionBar: () =>
                          FormDateTimePickerActionBar({
                            value,
                            onChange,
                            handleClose,
                          }),
                        tabs: FormDateTimePickerTabs,
                      }}
                      onClose={handleClose}
                    />
                  )}
                </PopoverController>
              </Stack>
            </LocalizationProvider>
            {error && (
              <Alert color="red" text={error.message || t('ui:invalid_date')} />
            )}
          </Stack>
          {hint && <Alert color="blue" text={hint} />}
        </FormDateTimePickerContainer>
      )}
    />
  );
};
